import api, { apiError } from "@/lib/api";
import { fileUrl } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { ArrowSquareOut, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LAYOUTS } from "@/components/demo/layouts";

const PARTS = { capa: "Capa", fotos: "Fotos", textos: "Textos", telemovel: "Telemóvel" };

function Qc({ qc }) {
  if (!qc) return null;
  return (
    <div className="space-y-2 text-sm" data-testid="demo-qc">
      <div className={`flex items-center gap-1.5 font-semibold ${qc.passed ? "text-emerald-700" : "text-amber-700"}`} data-testid="demo-qc-score">
        {qc.passed ? <CheckCircle weight="fill" /> : <WarningCircle weight="fill" />}
        Nota do Designer: {qc.score}/100 {qc.passed ? "" : "(abaixo de 80 — reveja com atenção)"} · {qc.attempts?.length || 1} tentativa(s)
      </div>
      <div className="grid grid-cols-4 gap-1">{Object.entries(PARTS).map(([k, l]) => <div key={k} className="rounded-lg bg-secondary px-2 py-1 text-xs">{l}: <b>{qc.parts?.[k] ?? "—"}</b>/25</div>)}</div>
      {qc.issues?.length > 0 && <ul className="list-disc pl-5 text-xs text-muted-foreground" data-testid="demo-qc-issues">{qc.issues.map((x) => <li key={x}>{x}</li>)}</ul>}
    </div>
  );
}

function Palette({ site }) {
  const pal = site.palette || {};
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="demo-style">
      Estilo: <b className="text-foreground">{LAYOUTS[site.layout]?.label || site.layout}</b>
      {["primary", "accent", "dark"].map((k) => pal[k] && <span key={k} title={pal[k]} className="h-4 w-4 rounded-full border" style={{ background: pal[k] }} />)}
    </div>
  );
}

export function DemoReview({ p, onChange }) {
  if (!p.demo_url) {
    return <p className="text-sm text-muted-foreground">{p.demo_deleted_at ? `Demonstração apagada (${p.demo_deleted_reason}).` : "Sem demonstração."}</p>;
  }
  const site = p.demo?.site || {};
  const qc = p.demo?.qc;
  const approve = async (approved) => {
    try { await api.post(`/prospect/prospects/${p.id}/approve-demo`, { approved }); toast.success(approved ? "Demonstração aprovada" : "Aprovação retirada"); onChange(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      <a href={`${p.demo_url}?preview=1`} target="_blank" rel="noreferrer" className="flex items-center gap-1 break-all text-sm font-semibold text-primary underline" data-testid="demo-link">{p.demo_url} <ArrowSquareOut /></a>
      <div className="flex flex-col items-start gap-4 sm:flex-row">
        {qc?.screenshot_id ? (
          <a href={fileUrl(qc.screenshot_id)} target="_blank" rel="noreferrer" className="mx-auto w-48 shrink-0 overflow-hidden rounded-[1.6rem] border-[6px] border-zinc-900 shadow-lg sm:mx-0" data-testid="demo-mobile-shot">
            <img src={fileUrl(qc.screenshot_id)} alt="Primeiro ecrã no telemóvel" className="w-full" />
          </a>
        ) : <p className="w-48 shrink-0 rounded-xl border border-dashed p-4 text-xs text-muted-foreground" data-testid="demo-no-shot">Captura do telemóvel indisponível — abra o link para rever.</p>}
        <div className="min-w-0 flex-1 space-y-3">
          <Qc qc={qc} />
          <Palette site={site} />
          {site.tagline && <p className="text-sm italic">“{site.tagline}”</p>}
          <p className="text-xs text-muted-foreground" data-testid="demo-views">Aberta {p.demo_views || 0} vezes (as suas visitas daqui não contam)</p>
          {p.demo_expires_at && <p className="text-xs text-muted-foreground" data-testid="demo-expiry">Apagada automaticamente a {new Date(p.demo_expires_at).toLocaleDateString("pt-PT")} se não houver resposta.</p>}
          {p.demo_approved ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 text-sm font-semibold text-emerald-700" data-testid="demo-approved-badge"><CheckCircle weight="fill" /> Aprovada por si</span>
              <Button size="sm" variant="ghost" onClick={() => approve(false)} data-testid="demo-unapprove-btn">Retirar aprovação</Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => approve(true)} data-testid="demo-approve-btn"><CheckCircle className="mr-1" /> Aprovar demonstração</Button>
          )}
        </div>
      </div>
    </div>
  );
}
